import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import ImageWithBasePath from "../../../../core/common/imageWithBasePath";
import {
  gender,
  SORT_OPTIONS,
  status,
} from "../../../../core/common/selectoption/selectoption";
import CommonSelect, { Option } from "../../../../core/common/commonSelect";
import TooltipOption from "../../../../core/common/tooltipOption";
import { formatDate } from "../../../../utils/dateutils";
import { useReactToPrint } from "react-to-print";
import * as XLSX from "xlsx";
import { Class, getAllClasses } from "../../../../service/classes";
import { Spin } from "antd";
import { getAdmissions } from "../../../../service/admission";
import AdmissionModals from "../admissionModals";
import { selectSystemsForCurrentBranch } from "../../../../core/data/redux/slice/systemsSlice";
import { selectCurrentBranch } from "../../../../core/data/redux/slice/branchesSlice";
import { campusBrandName, campusBranchLabel } from "../../../../utils/campusLogos";
import { useSelector } from "react-redux";

export interface FeeRecord {
  _id: string;
  discount: number;
  status: string;
  invoiceNumber: string;
  total: number;
  subTotal: number;
  paymentType: string;
  dueDate: string | null;
  branch: string;
  session: string;
  system: string;
  class: string;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
  enrollment: string;
  student: {
    grNumber: string;
  };
  admission: {
    appliedClass: {
      name: string;
    };
    _id: string;
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    gender: string;
    bloodGroup: string;
    religion: string;
    email: string;
    motherTongue: string;
    languageKnown: string[];
    hasMedicalIssue: boolean;
    nationality: string;
    bFormNo: string;
    placeOfBirth: string;
    mobileNO: string;
    hasSiblings: boolean;
    noOfSiblings: number;
    isVaccinated: boolean;
    fatherName: string;
    fatherCnic: string;
    fatherEmail: string;
    isFatherVaccinated: boolean;
    isMotherVaccinated: boolean;
    isGuardianVaccinated: boolean;
    city: string;
    area: string;
    houseNo: string;
    system: string;
    prevSchoolName: string;
    prevSchoolAddress: string;
    prevClass: string;
    lastResult: string;
    transferCertificate: string;
    birthCertificate: string;
    section: {
      name: string;
      _id: string;
    };
    status: string;
    branch: string;
    session: string;
    isDeleted: boolean;
    createdAt: string;
    updatedAt: string;
    __v: number;
    invoice: string;
    studentPicture: string;
  };
  notes: string;
  invoiceItems: Array<{
    _id: string;
    feeType: { name: string };
    feeGroup: { name: string };
    amount: number;
    discount: number;
    description: string;
    createdAt: string;
    updatedAt: string;
    __v: number;
    invoice: string;
  }>;
}

export interface AdmissionData {
  _id: string;
  registrationNumber: string;
  firstName: string;
  lastName: string;
  gender: string;
  status: string;
  enrollment: string;
  createdAt: string;
  dateOfBirth: string;
  fatherEmail: string;
  fatherPhoneNumber: string;
  temporaryGR: string;
  fatherName: string;
  studentPicture: string;
  feeGroups: Array<{
    _id: string;
    name: string;
  }>;
  feeTypes: Array<{
    _id: string;
    name: string;
    amount: number;
  }>;
  appliedClass: {
    name: string;
  };
  section: {
    name: string;
    _id: string;
  };
  invoice?: {
    _id: string;
    discount: {
      profile: number;
      manual: number;
    };
    status: string;
    invoiceNumber: string;
    total: number;
    subTotal: number;
    paymentType: string;
    dueDate: string | null;
    invoiceItems: Array<{
      _id: string;
      feeType: { name: string };
      feeGroup: { name: string };
      amount: number;
      discount: number;
      description: string;
    }>;
    notes?: string;
  };
}

type PaginationDataType = {
  currentPage: number;
  hasNextPage?: boolean;
  hasPrevPage?: boolean;
  limit: number;
  total?: number;
  totalPages?: number;
};

const AdmissionGrid = () => {
  const routes = all_routes;
  const systems = useSelector(selectSystemsForCurrentBranch);
  const currentBranch = useSelector(selectCurrentBranch);
  const componentRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<AdmissionData[]>([]);
  const [paginationData, setPaginationData] = useState<PaginationDataType>({
    currentPage: 1,
    limit: 10,
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [classes, setAllClasses] = useState<Option[]>([]);
  const [classesLoading, setClassesLoading] = useState(false);
  const [selectedAdmission, setSelectedAdmission] =
    useState<AdmissionData | null>(null);
  const [filters, setFilters] = useState<{
    class?: Option;
    gender?: Option;
    status?: Option;
  } | null>(null);

  const [appliedFilters, setAppliedFilters] = useState<{
    class?: string;
    gender?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
    system?: string;
  } | null>({ system: systems[0]?._id });

  const [sortBy, setSortBy] = useState(SORT_OPTIONS[0]);
  const [applyLoading, setApplyLoading] = useState(false);

  const dropdownMenuRef = useRef<HTMLDivElement | null>(null);
  const skipFetchApi = useRef(true);

  const fetchStudents = async () => {
    if (skipFetchApi.current) {
      skipFetchApi.current = false;
      return;
    }
    try {
      setLoading(true);
      const response = await getAdmissions({
        page: paginationData.currentPage,
        limit: paginationData.limit,
        ...appliedFilters,
        ...sortBy.value,
      });
      if (paginationData.currentPage === 1) {
        setData(response.data);
      } else {
        setData((prev) => [...prev, ...response.data]);
      }
      setPaginationData(response.pagination);
    } catch (error) {
      console.error("Failed to fetch students:", error);
    } finally {
      setLoading(false);
    }
  };

  const refetchStudents = async () => {
    setLoading(true);
    try {
      const response = await getAdmissions({
        page: 1,
        limit: paginationData.limit,
        ...appliedFilters,
        ...sortBy.value,
      });
      setData(response.data);
      setPaginationData({
        ...response.pagination,
        currentPage: 1,
      });
    } catch (error) {
      console.error("Failed to fetch students:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async () => {
    setClassesLoading(true);
    try {
      const response = await getAllClasses();
      const formattedClasses = response.classes.map((classItem: Class) => ({
        label: classItem.name,
        value: classItem._id,
      }));
      setAllClasses(formattedClasses);
    } catch (error) {
      console.error("Error fetching classes:", error);
    } finally {
      setClassesLoading(false);
    }
  };

  useEffect(() => {
    fetchClasses();
  }, []);

  useEffect(() => {
    refetchStudents();
  }, [appliedFilters, sortBy]);

  useEffect(() => {
    fetchStudents();
  }, [paginationData.currentPage]);

  const handleApplyClick = async () => {
    setApplyLoading(true);
    if (dropdownMenuRef.current) {
      dropdownMenuRef.current.classList.remove("show");
    }
    try {
      setAppliedFilters({
        class: filters?.class?.value as string,
        gender: filters?.gender?.value as string,
        status: filters?.status?.value as string,
        system: appliedFilters?.system,
      });
    } finally {
      setApplyLoading(false);
    }
  };

  const handleLoadMore = async () => {
    if (loading || !paginationData.hasNextPage) return;
    setPaginationData((prev) => ({
      ...prev,
      currentPage: prev.currentPage + 1,
    }));
  };

  const handleResetFilters = () => {
    setFilters(null);
    setAppliedFilters({ system: systems[0]?._id });
  };

  const handleSortSelect = (item: {
    label: string;
    value: {
      sortBy: string;
      sortOrder: string;
    };
  }) => {
    setSortBy(item);
  };

  const handlePrint = useReactToPrint({ contentRef: componentRef });

  const handleExportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(
      data.map((item) => ({
        "Registration No": item.registrationNumber,
        "Student Name": `${item.firstName} ${item.lastName}`,
        Class: item.appliedClass.name,
        Section: item.section.name,
        Gender: item.gender,
        Status: item.status,
        "Applied On": formatDate(item.createdAt),
      }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Admissions");
    XLSX.writeFile(wb, "admissions.xlsx");
  };

  return (
    <div className="page-wrapper">
      <div className="content content-two">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Admissions</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">Admission</li>
                <li className="breadcrumb-item active" aria-current="page">
                  Admission Form Grid
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <TooltipOption
              onRefresh={refetchStudents}
              onExportPdf={handlePrint}
              onExportExcel={handleExportExcel}
            />
            <div className="mb-2">
              <Link
                to={routes.addAdmissionForm}
                className="btn btn-primary d-flex align-items-center"
              >
                <i className="ti ti-square-rounded-plus me-2" />
                New Admission
              </Link>
            </div>
          </div>
        </div>

        <div className="bg-white p-3 border rounded-1 d-flex align-items-center justify-content-between flex-wrap mb-4 pb-0">
          <h4 className="mb-3">Admission Form Grid</h4>
          <div className="d-flex align-items-center flex-wrap">
            <div className="mb-3 me-2">
              <CommonSelect
                className="select"
                options={systems}
                value={systems.find(
                  (item) => item._id === appliedFilters?.system
                )}
                getOptionValue={(item) => item._id}
                getOptionLabel={(item) => item.name}
                onChange={(systemItem) =>
                  setAppliedFilters((prev) => ({
                    ...prev,
                    system: systemItem?._id,
                  }))
                }
              />
            </div>

            <div className="dropdown mb-3 me-2">
              <Link
                to="#"
                className="btn btn-outline-light bg-white dropdown-toggle"
                data-bs-toggle="dropdown"
                data-bs-auto-close="outside"
              >
                <i className="ti ti-filter me-2" />
                Filter
              </Link>
              <div className="dropdown-menu drop-width" ref={dropdownMenuRef}>
                <form>
                  <div className="d-flex align-items-center border-bottom p-3">
                    <h4>Filter</h4>
                  </div>
                  <div className="p-3 pb-0 border-bottom">
                    <div className="row">
                      <div className="col-md-12">
                        <div className="mb-3">
                          <label className="form-label">Class</label>
                          {classesLoading ? (
                            <div> Loading classes... </div>
                          ) : (
                            <CommonSelect
                              className="select"
                              options={classes}
                              value={filters?.class}
                              onChange={(classItem) =>
                                setFilters((prev) => ({
                                  ...prev,
                                  class: classItem as Option,
                                }))
                              }
                            />
                          )}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Gender</label>
                          <CommonSelect
                            className="select"
                            options={gender}
                            value={filters?.gender}
                            onChange={(genderItem) =>
                              setFilters((prev) => ({
                                ...prev,
                                gender: genderItem as Option,
                              }))
                            }
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Status</label>
                          <CommonSelect
                            className="select"
                            options={status}
                            value={filters?.status}
                            onChange={(statusItem) =>
                              setFilters((prev) => ({
                                ...prev,
                                status: statusItem as Option,
                              }))
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="p-3 d-flex align-items-center justify-content-end">
                    <button
                      type="button"
                      className="btn btn-light me-3"
                      onClick={handleResetFilters}
                    >
                      Reset
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleApplyClick}
                      disabled={applyLoading}
                    >
                      {applyLoading ? (
                        <>
                          <i className="ti ti-loader-3 me-2 spin-animation" />
                          Applying...
                        </>
                      ) : (
                        "Apply"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
            <div className="d-flex align-items-center bg-white border rounded-2 p-1 mb-3 me-2">
              <Link
                to={routes.admissionFormList}
                className="btn btn-icon btn-sm me-1 bg-light primary-hover"
              >
                <i className="ti ti-list-tree" />
              </Link>
              <Link
                to={routes.admissionFormGrid}
                className="active btn btn-icon btn-sm primary-hover"
              >
                <i className="ti ti-grid-dots" />
              </Link>
            </div>
            <div className="dropdown mb-3">
              <Link
                to="#"
                className="btn btn-outline-light bg-white dropdown-toggle"
                data-bs-toggle="dropdown"
              >
                <i className="ti ti-sort-ascending-2 me-2" />
                Sort by {sortBy.label}
              </Link>
              <ul className="dropdown-menu p-3">
                {SORT_OPTIONS.map((item, index) => (
                  <li key={index}>
                    <button
                      className={`dropdown-item rounded-1 ${
                        item.label === sortBy.label ? "active" : ""
                      }`}
                      style={{ cursor: "pointer" }}
                      onClick={() => handleSortSelect(item)}
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="row printable-content" ref={componentRef}>
          <div className="print-only-header">
            <div className="school-brand">
              <img src={currentBranch?.blackLogoUrl || "/assets/img/icons/logo.png"} alt={campusBrandName(currentBranch)} />
              <strong>{campusBrandName(currentBranch)}{campusBranchLabel(currentBranch) ? ` - ${campusBranchLabel(currentBranch)}` : ""}</strong>
            </div>
            <div className="report-title">Admission Grid</div>
            <div className="print-date">{new Date().toLocaleDateString("en-GB")}</div>
          </div>
          {loading && paginationData.currentPage === 1 ? (
            <div className="col-12 d-flex justify-content-center">
              <Spin size="large" />
            </div>
          ) : data.length > 0 ? (
            data.map((admission) => (
              <div
                className="col-xxl-3 col-xl-4 col-md-6 d-flex"
                key={admission._id}
              >
                <div className="card flex-fill">
                  <div className="card-header d-flex align-items-center justify-content-between">
                    <Link
                      to={routes.admissionFormDetail}
                      className="link-primary"
                      state={admission}
                    >
                      {admission.registrationNumber}
                    </Link>
                    <div className="d-flex align-items-center text-nowrap">
                      <span
                        className={`badge d-inline-flex align-items-center me-1 ${
                          admission.status.toLowerCase() === "admitted"
                            ? "badge-soft-success"
                            : admission.status.toLowerCase() === "rejected"
                            ? "badge-soft-danger"
                            : "badge-soft-warning"
                        }`}
                      >
                        <i className="ti ti-circle-filled fs-5 me-1" />
                        {admission.status}
                      </span>
                      <div className="dropdown">
                        <Link
                          to="#"
                          className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
                          data-bs-toggle="dropdown"
                          aria-expanded="false"
                        >
                          <i className="ti ti-dots-vertical fs-14" />
                        </Link>
                        <ul className="dropdown-menu dropdown-menu-end p-3">
                          <li>
                            <Link
                              className="dropdown-item rounded-1"
                              to={routes.admissionFormDetail}
                              state={admission}
                            >
                              <i className="ti ti-menu me-2" />
                              View Form
                            </Link>
                          </li>
                          <li>
                            <Link
                              className="dropdown-item rounded-1"
                              to={routes.editAdmissionForm}
                              state={admission}
                            >
                              <i className="ti ti-edit-circle me-2" />
                              Edit
                            </Link>
                          </li>
                          <li>
                            <Link
                              className="dropdown-item rounded-1"
                              to="#"
                              data-bs-toggle="modal"
                              data-bs-target="#delete-modal"
                              onClick={() => setSelectedId(admission._id)}
                            >
                              <i className="ti ti-trash-x me-2" />
                              Delete
                            </Link>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="bg-light-300 rounded-2 p-3 mb-3">
                      <div className="d-flex align-items-center">
                        <div className="avatar avatar-lg flex-shrink-0">
                          <ImageWithBasePath
                            src={"assets/img/students/student-01.jpg"}
                            fullSrc={admission.studentPicture}
                            className="img-fluid rounded-circle"
                            alt="Student"
                          />
                        </div>
                        <div className="ms-2">
                          <h5 className="mb-0">
                            {admission.firstName} {admission.lastName}
                          </h5>
                        </div>
                      </div>
                    </div>
                    <div className="d-flex align-items-center justify-content-between gx-2">
                      <div>
                        <p className="mb-0">Applied Class</p>
                        <p className="text-dark">
                          {admission.appliedClass.name}
                        </p>
                      </div>
                      <div>
                        <p className="mb-0">Gender</p>
                        <p className="text-dark">{admission.gender}</p>
                      </div>
                      <div>
                        <p className="mb-0">Applied On</p>
                        <p className="text-dark">
                          {formatDate(admission.createdAt)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="card-footer d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <Link
                        to={`tel:${admission.fatherPhoneNumber}`}
                        className="btn btn-outline-light bg-white btn-icon d-flex align-items-center justify-content-center rounded-circle p-0 me-2"
                      >
                        <i className="ti ti-phone" />
                      </Link>
                      <Link
                        to={`mailto:${admission.fatherEmail}`}
                        className="btn btn-outline-light bg-white btn-icon d-flex align-items-center justify-content-center rounded-circle p-0 me-3"
                      >
                        <i className="ti ti-mail" />
                      </Link>
                    </div>
                    <div className="d-flex gap-1">
                      <Link
                        to="#"
                        data-bs-toggle="modal"
                        data-bs-target="#add_fees_collect"
                        className="btn btn-primary btn-sm fw-semibold"
                        onClick={() => setSelectedAdmission(admission)}
                      >
                        Fee Voucher
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-12">
              <div className="card">
                <div className="card-body text-center py-5">
                  <div className="empty-state-icon">
                    <i className="ti ti-user-x fs-1 text-muted"></i>
                  </div>
                  <h4 className="text-muted mt-3">No Admissions Found</h4>
                  <p className="text-muted mb-4">
                    {appliedFilters
                      ? "No admissions match the current filter criteria."
                      : "There are no admissions in the system yet."}
                  </p>
                  {appliedFilters && (
                    <button
                      className="btn btn-primary"
                      onClick={handleResetFilters}
                    >
                      <i className="ti ti-filter-off me-2"></i>
                      Clear All Filters
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {paginationData?.hasNextPage && (
          <div className="col-md-12 text-center mt-4">
            <button
              className="btn btn-primary"
              onClick={handleLoadMore}
              disabled={loading}
            >
              {loading ? (
                <>
                  <i className="ti ti-loader-3 me-2 spin-animation" />
                  Loading...
                </>
              ) : (
                <>
                  <i className="ti ti-loader-3 me-2" />
                  Load More
                </>
              )}
            </button>
          </div>
        )}
      </div>

      <AdmissionModals
        selectedId={selectedId}
        setSelectedId={setSelectedId}
        fetchData={refetchStudents}
        admissionData={selectedAdmission}
      />
    </div>
  );
};

export default AdmissionGrid;
